import { useParams, Link, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MobileBottomBar from '@/components/feature/MobileBottomBar';
import WorksGallery from '@/components/feature/WorksGallery';
import { PRODUCT_DATA, CITY_DATA, ALL_CITIES, ALL_PRODUCTS } from '@/mocks/productCityData';

const FAQ_ITEMS = [
  {
    q: 'Сколько стоит выезд на замер?',
    a: 'Замер бесплатный при последующем заказе. Если замер нужен без заказа — стоимость обсуждается индивидуально, обычно от 500 до 1500 ₽ в зависимости от удалённости.',
  },
  {
    q: 'Какие сроки изготовления?',
    a: 'Стандартный срок — от 5 до 14 дней в зависимости от сложности. Крупные конструкции (навесы, беседки) могут занять до 3 недель. Точный срок называем после замера.',
  },
  {
    q: 'Даёте ли гарантию?',
    a: 'Да, гарантия на каркас и монтаж — 100%. Если что-то не так, приедем и исправим. На покраску действует гарантия от производителя краски (обычно 5–10 лет).',
  },
  {
    q: 'Работаете ли по договору?',
    a: 'Да, со всеми клиентами заключаем договор с фиксированной сметой. Цена не меняется в процессе работы.',
  },
];

function getRandomCities(exclude: string, count: number): typeof ALL_CITIES {
  const filtered = ALL_CITIES.filter((c) => c.name !== exclude);
  const shuffled = [...filtered].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

function getRandomProducts(exclude: string, count: number): typeof ALL_PRODUCTS {
  const filtered = ALL_PRODUCTS.filter((p) => p.slug !== exclude);
  const shuffled = [...filtered].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

export default function ProductCityPage() {
  const { productSlug, citySlug } = useParams<{ productSlug: string; citySlug: string }>();

  if (!productSlug || !citySlug) {
    return <Navigate to="/" replace />;
  }

  const product = PRODUCT_DATA[productSlug];
  const city = CITY_DATA[citySlug];

  if (!product || !city) {
    return <Navigate to="/" replace />;
  }

  const otherCities = getRandomCities(city.name, 8);
  const otherProducts = getRandomProducts(productSlug, 4);

  const pageTitle = `${product.name} ${city.prepositional} — изготовление и монтаж под ключ`;
  const pageDescription = `${product.name} ${city.prepositional}. ${product.description} Выезд на замер, расчёт стоимости, монтаж. Работаем в ${city.prepositional} и по всему району.`;

  return (
    <>
      <Navbar />
      <main className="pb-16 md:pb-0">
        {/* Hero */}
        <section className="relative min-h-[60vh] md:min-h-[70vh] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={product.heroBgUrl}
              alt=""
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/65"></div>
          </div>

          <div className="relative z-10 w-full px-4 md:px-6 pt-24 pb-16 md:pt-32 md:pb-20">
            <div className="mx-auto text-center" style={{ maxWidth: '800px' }}>
              <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight mb-5 animate-fade-in-up text-balance">
                {product.name} {city.prepositional}
              </h1>
              <p className="text-base md:text-lg text-background-50/85 max-w-2xl mx-auto mb-8 animate-fade-in-up leading-relaxed" style={{ animationDelay: '0.15s' }}>
                {product.description} Изготовление и монтаж под ключ {city.prepositional} и районе.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-12 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
                <a
                  href="https://wa.me/79174131562"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="whitespace-nowrap w-full sm:w-auto bg-[#25D366] hover:bg-[#1eb851] text-background-50 font-semibold text-base px-7 py-3.5 rounded-md transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] cursor-pointer inline-flex items-center justify-center gap-2.5"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-whatsapp-line text-lg"></i>
                  </span>
                  Рассчитать в WhatsApp
                </a>
                <a
                  href="tel:+79174131562"
                  className="whitespace-nowrap w-full sm:w-auto border-2 border-background-50/40 hover:border-background-50/70 text-background-50 font-semibold text-base px-7 py-3.5 rounded-md transition-all duration-300 hover:bg-background-50/10 cursor-pointer text-center flex items-center justify-center gap-2.5"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-phone-fill text-lg"></i>
                  </span>
                  Позвонить
                </a>
              </div>

              {/* Hero benefit cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.45s' }}>
                {product.benefits.map((item) => (
                  <div
                    key={item.title}
                    className="flex items-start gap-3 bg-background-50/10 backdrop-blur-sm border border-background-50/15 rounded-lg p-4 text-left"
                  >
                    <div className="shrink-0 w-9 h-9 flex items-center justify-center rounded-full bg-primary-500/20 text-primary-300">
                      <i className={`${item.icon} text-base`}></i>
                    </div>
                    <div>
                      <h3 className="font-heading text-sm font-semibold text-background-50 mb-0.5">{item.title}</h3>
                      <p className="text-xs text-background-50/65 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* How we work */}
        <section className="py-14 md:py-18 bg-background-100">
          <div className="mx-auto px-4 md:px-6" style={{ maxWidth: '1000px' }}>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-10 text-center">
              Как мы работаем {city.prepositional}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { step: '01', icon: 'ri-phone-line', title: 'Звонок или заявка', desc: 'Вы оставляете заявку или звоните. Обсуждаем задачу, называем примерную стоимость.' },
                { step: '02', icon: 'ri-map-pin-line', title: 'Выезд на замер', desc: `Приезжаем к вам ${city.prepositional}, делаем точные замеры, оцениваем условия.` },
                { step: '03', icon: 'ri-tools-line', title: 'Изготовление', desc: 'В собственном цеху изготавливаем конструкцию по вашим размерам.' },
                { step: '04', icon: 'ri-check-double-line', title: 'Монтаж', desc: `Привозим и монтируем ${city.prepositional}. Вы принимаете готовый объект.` },
              ].map((item) => (
                <div key={item.step} className="bg-background-50 border border-background-200/60 rounded-lg p-5 md:p-6 text-center">
                  <div className="w-11 h-11 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3">
                    <i className={`${item.icon} text-xl`}></i>
                  </div>
                  <div className="text-xs font-bold text-primary-500 mb-1.5">{item.step}</div>
                  <h3 className="font-heading text-base font-semibold text-foreground-950 mb-1.5">{item.title}</h3>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Description + advantages */}
        <section className="py-14 md:py-18 bg-background-50">
          <div className="mx-auto px-4 md:px-6" style={{ maxWidth: '900px' }}>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-8 text-center">
              Почему выбирают {product.name.toLowerCase()} {city.prepositional}
            </h2>
            <div className="flex flex-col gap-4">
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                <strong>Художественная ковка</strong> — мастерская металла с собственным производством. Изготавливаем {product.shortName} {city.prepositional} по индивидуальным размерам. Выезжаем на замер, делаем точный расчёт, привозим и монтируем. Более 10 лет опыта, 500+ готовых объектов в регионе.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                {city.name} — один из городов нашей зоны обслуживания. Мы регулярно выезжаем сюда на замеры и монтаж. При большом объёме заказа выезд на замер бесплатный. Работаем официально, с договором и фиксированной сметой.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                {[
                  { icon: 'ri-file-text-line', title: 'Договор', desc: 'Фиксируем цену и сроки в договоре — никаких сюрпризов' },
                  { icon: 'ri-coins-line', title: 'Прозрачная смета', desc: 'Вы знаете, за что платите — все позиции расписаны' },
                  { icon: 'ri-shield-check-line', title: 'Гарантия 100%', desc: 'На каркас и монтаж — если что-то не так, приедем и исправим' },
                  { icon: 'ri-truck-line', title: 'Доставка', desc: `${city.prepositional} — привозим своим транспортом, без посредников` },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-3 bg-background-100 border border-background-200/60 rounded-lg p-4">
                    <div className="shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className={`${item.icon} text-lg`}></i>
                    </div>
                    <div>
                      <h3 className="font-heading text-sm font-semibold text-foreground-950 mb-0.5">{item.title}</h3>
                      <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Works Gallery */}
        <WorksGallery productName={product.shortName} />

        {/* CTA */}
        <section className="py-14 md:py-18 bg-background-100">
          <div className="mx-auto px-4 md:px-6 text-center" style={{ maxWidth: '700px' }}>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              Хотите {product.shortName} {city.prepositional}?
            </h2>
            <p className="text-sm md:text-base text-foreground-600 mb-7">
              Позвоните или напишите — рассчитаем стоимость, ответим на вопросы и договоримся о выезде на замер.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://wa.me/79174131562"
                target="_blank"
                rel="noopener noreferrer"
                className="whitespace-nowrap w-full sm:w-auto bg-[#25D366] hover:bg-[#1eb851] text-background-50 font-semibold text-base px-8 py-3.5 rounded-md transition-all duration-300 cursor-pointer inline-flex items-center justify-center gap-2"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-whatsapp-line text-lg"></i>
                </span>
                Написать в WhatsApp
              </a>
              <a
                href="https://t.me/Gmgmg0202"
                target="_blank"
                rel="noopener noreferrer"
                className="whitespace-nowrap w-full sm:w-auto bg-[#2AABEE] hover:bg-[#1e96d4] text-background-50 font-semibold text-base px-8 py-3.5 rounded-md transition-all duration-300 cursor-pointer inline-flex items-center justify-center gap-2"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-telegram-line text-lg"></i>
                </span>
                Написать в Telegram
              </a>
              <a
                href="https://max.ru/u/f9LHodD0cOICoHyOC2LT2Vx_42oC8Zdm2VV9Uv5O-6mGvkZgeZzUcJQkER0"
                target="_blank"
                rel="noopener noreferrer"
                className="whitespace-nowrap w-full sm:w-auto bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold text-base px-8 py-3.5 rounded-md transition-all duration-300 cursor-pointer inline-flex items-center justify-center gap-2"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-chat-3-line text-lg"></i>
                </span>
                Перейти в MAX
              </a>
              <a
                href="tel:+79174131562"
                className="whitespace-nowrap w-full sm:w-auto border-2 border-foreground-200 hover:border-primary-400 text-foreground-800 font-semibold text-base px-8 py-3.5 rounded-md transition-all duration-300 cursor-pointer text-center flex items-center justify-center gap-2"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-phone-fill text-lg"></i>
                </span>
                Позвонить
              </a>
            </div>
          </div>
        </section>

        {/* Other cities for this product */}
        <section className="py-14 md:py-18 bg-background-50">
          <div className="mx-auto px-4 md:px-6" style={{ maxWidth: '1000px' }}>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2 text-center">
              {product.name} в других городах
            </h2>
            <p className="text-sm text-foreground-500 mb-8 text-center">
              Работаем по всему юго-западу Башкортостана и соседним регионам
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {otherCities.map((c) => (
                <Link
                  key={c.name}
                  to={`/products/${productSlug}/${Object.keys(CITY_DATA).find((k) => CITY_DATA[k].name === c.name)}`}
                  className="flex items-center gap-2 bg-background-100 border border-background-200/50 rounded-md px-3.5 py-2.5 hover:border-primary-200/60 hover:bg-primary-50/30 transition-all duration-200 cursor-pointer"
                >
                  <span className="w-4 h-4 flex items-center justify-center text-primary-400">
                    <i className="ri-map-pin-line text-xs"></i>
                  </span>
                  <span className="text-sm text-foreground-700">{product.name} {c.prepositional}</span>
                </Link>
              ))}
              <Link
                to={`/products/${productSlug}`}
                className="flex items-center gap-2 bg-background-100 border border-background-200/50 rounded-md px-3.5 py-2.5 hover:border-primary-200/60 hover:bg-primary-50/30 transition-all duration-200 cursor-pointer col-span-full sm:col-span-1 justify-center"
              >
                <span className="text-sm text-foreground-700 font-medium">Все города →</span>
              </Link>
            </div>
          </div>
        </section>

        {/* Other products in this city */}
        <section className="py-14 md:py-18 bg-background-100">
          <div className="mx-auto px-4 md:px-6" style={{ maxWidth: '1000px' }}>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2 text-center">
              Другие изделия {city.prepositional}
            </h2>
            <p className="text-sm text-foreground-500 mb-8 text-center">
              Посмотрите, что ещё мы делаем для жителей {city.name}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {otherProducts.map((p) => (
                <Link
                  key={p.slug}
                  to={`/products/${p.slug}/${citySlug}`}
                  className="flex items-center gap-3 bg-background-50 border border-background-200/50 rounded-lg px-4 py-3.5 hover:border-primary-200/60 hover:bg-primary-50/20 transition-all duration-200 cursor-pointer"
                >
                  <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 shrink-0">
                    <i className="ri-arrow-right-up-line text-base"></i>
                  </span>
                  <span className="text-sm font-medium text-foreground-800">{p.name} {city.prepositional}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-14 md:py-18 bg-background-50">
          <div className="mx-auto px-4 md:px-6" style={{ maxWidth: '800px' }}>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-10 text-center">
              Частые вопросы о {product.name.toLowerCase()} {city.prepositional}
            </h2>
            <div className="flex flex-col gap-3">
              {FAQ_ITEMS.map((item, idx) => (
                <details key={idx} className="group bg-background-100 border border-background-200/60 rounded-lg overflow-hidden">
                  <summary className="flex items-center justify-between gap-3 p-4 md:p-5 cursor-pointer list-none">
                    <span className="text-sm md:text-base font-medium text-foreground-800 pr-2">{item.q}</span>
                    <span className="w-6 h-6 flex items-center justify-center shrink-0 text-foreground-400 group-open:rotate-180 transition-transform duration-200">
                      <i className="ri-arrow-down-s-line text-lg"></i>
                    </span>
                  </summary>
                  <div className="px-4 md:px-5 pb-4 md:pb-5 text-sm text-foreground-600 leading-relaxed">
                    {item.a}
                  </div>
                </details>
              ))}
            </div>

            {/* Breadcrumbs-style footer */}
            <div className="mt-10 pt-6 border-t border-background-200/50 flex flex-wrap items-center gap-1.5 text-xs text-foreground-400">
              <Link to="/" className="hover:text-primary-500 transition-colors">Главная</Link>
              <span>/</span>
              <Link to={`/products/${productSlug}`} className="hover:text-primary-500 transition-colors">{product.name}</Link>
              <span>/</span>
              <span className="text-foreground-600">{city.name}</span>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <MobileBottomBar />
    </>
  );
}
